<template>
  <div v-adjust-height class="flex flex-col space-y-4 bg-gray-50 overflow-auto">
    <div class="prose px-6 pt-5 text-sm text-gray-400 cursor-pointer">
      <h1 class="text-xl font-medium text-secondary [&>a]:no-underline [&>a]:text-secondary">
        <a href="#">Mobiliseringer</a> / Min plan
      </h1>
    </div>

    <div class="bg-white px-6 py-5 text-gray-400">
      <div class="flex items-center">
        <h2 class="flex-auto font-semibold text-gray-900">{{ currentDateDisplay }}</h2>
        <button @click="previousMonth" type="button" class="-my-1.5 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
          <span class="sr-only">Forrige måned</span>
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd"
              d="M12.79 5.23a.75.75 0 01-.02 1.06L8.832 10l3.938 3.71a.75.75 0 11-1.04 1.08l-4.5-4.25a.75.75 0 010-1.08l4.5-4.25a.75.75 0 011.06.02z"
              clip-rule="evenodd" />
          </svg>
        </button>
        <button @click="nextMonth" type="button" class="-my-1.5 -mr-1.5 ml-2 flex flex-none items-center justify-center p-1.5 text-gray-400 hover:text-gray-500">
          <span class="sr-only">Næste måned</span>
          <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd"
              d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z"
              clip-rule="evenodd" />
          </svg>
        </button>
      </div>
      <div class="mt-10 grid grid-cols-7 text-center text-sm font-bold leading-6 text-gray-900">
        <div v-for="weekDay in weekDays" :key="weekDay">{{ weekDay }}</div>
      </div>
      <div class="isolate mt-2 grid grid-cols-7 gap-px bg-gray-200 text-sm shadow ring-1 ring-gray-200">
        <button v-for="day in daysForGrid" :key="day.date.toString()" class="relative h-10 focus:z-10" :class="isSelectedDay(day.date)
            ? 'bg-secondary text-white'
            : (day.isCurrentMonth ? 'bg-white text-gray-900' : 'bg-gray-50 text-gray-400')"
          @click="setSelectedDay(day.date)" type="button">
          <time :datetime="day.date.toISOString()" class="mx-auto flex h-7 w-7 items-center justify-center rounded-full" :class="day.date.getDate() === new Date().getDate() && day.date.getMonth() === new Date().getMonth() ? 'font-semibold' : ''">
            {{ day.date.getDate() }}
          </time>
          <div class="absolute top-0 left-0 w-full h-full flex flex-row gap-x-[2px] items-end justify-center pb-1">
            <div v-for="dotIndex in getDotCount(day.date)" :key="dotIndex" class="w-1 h-1 rounded-full bg-orange-600">
            </div>
          </div>
        </button>
      </div>
    </div>

    <div v-if="selectedDay">
      <div v-if="mobilizationDetails && mobilizationDetails.length > 0">
        <div v-for="(detail, index) in mobilizationDetails" :key="index" class="bg-white px-6 py-5 text-sm text-gray-400 mb-12">
          <h1 class="text-xl font-medium text-secondary pb-4">{{ displayFormattedDate(selectedDay) }}</h1>
          <ul role="list" class="divide-y divide-gray-200 border-y border-gray-200 p-0">
            <li class="m-0 flex justify-between p-0">
              <div class="m-0 flex flex-col pb-2 pt-2">
                <div class="text-sm font-medium text-blue-900">Site</div>
                <div class="text-base font-medium leading-5 text-gray-800">{{ detail.siteName }} - {{ detail.kundeNavn }}</div>
              </div>
            </li>
            <li class="m-0 flex justify-between p-0">
              <div class="m-0 flex flex-col pb-2 pt-2">
                <div class="text-sm font-medium text-blue-900">Arb. art</div>
                <div class="text-base font-medium leading-5 text-gray-800">{{ detail.workType }}</div>
              </div>
            </li>
            <li class="m-0 flex justify-between p-0">
              <div class="m-0 flex flex-col pb-2 pt-2">
                <div class="text-sm font-medium text-blue-900">Fra dato</div>
                <div class="text-base font-medium leading-5 text-gray-800">{{ displayFormattedDate(detail.departureDate) }}
                </div>
              </div>
            </li>
            <li class="m-0 flex justify-between p-0">
              <div class="m-0 flex flex-col pb-2 pt-2">
                <div class="text-sm font-medium text-blue-900">Til dato</div>
                <div class="text-base font-medium leading-5 text-gray-800">{{ displayFormattedDate(detail.returnDate) }}</div>
              </div>
            </li>
            <li class="m-0 flex justify-between p-0">
              <div class="m-0 flex flex-col pb-2 pt-2">
                <div class="text-sm font-medium text-blue-900">Lønlinjer udspecificeret</div>
                <div v-if="detail.salary && detail.salary.length > 0" >
                  <div v-for="(salary, index) in detail.salary" :key="index" class="text-base font-medium leading-5 text-gray-800">
                    <div v-if="salary.antal !== 0" class="m-0 flex flex-row pb-2 pt-2">
                          <div class="m-0">{{ salary.timetype}}:</div>
                          <div class="m-0" style="margin-left: 5px;">{{ Number(salary.antal).toLocaleString() }} timer</div>
                    </div>
                    <div v-else-if="salary.antal === 0 && detail.salary.length == 1" class="text-base font-light text-blue-900">Der er ingen lønlinjer på denne dag.</div>
                  </div>
                </div>
                <div v-if="!detail.salary || detail.salary.length === 0" class="text-base font-light text-blue-900">Der er ingen lønlinjer på denne dag.</div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      <div v-else class="bg-white px-6 py-5 text-sm text-gray-400 mb-12">
        <h1 class="text-xl font-medium text-secondary pb-4">{{ displayFormattedDate(selectedDay) }}</h1>
        <div class="text-sm font-medium text-blue-900">Der er ingen mobiliseringer på denne dag.</div>
      </div>
    </div>
  </div>
</template>
<script lang="ts">
import { formatDateToString } from '@/core/utils'
import { computed, defineComponent, reactive, ref } from 'vue'
import { useStore } from 'vuex'
import applicationUserManager from '../core/applicationusermanager'

export default defineComponent({
  name: 'planView',
  async beforeMount () {
    const user = await applicationUserManager.getUserInfo()
    if (!user) {
      this.$router.push('/login')
    }
  },
  setup () {
    const store = useStore()
    const weekDays: string[] = ['Ma', 'Ti', 'On', 'To', 'Fr', 'Lø', 'Sø']
    const currentYear = ref(new Date().getFullYear())
    const currentMonth = ref(new Date().getMonth()) // Month starts at 0

    const currentDateDisplay = computed(() => {
      const date = new Date(currentYear.value, currentMonth.value)
      const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long' }
      const dateString = date.toLocaleDateString('da-DK', options)
      return dateString.charAt(0).toUpperCase() + dateString.slice(1)
    })

    const changeMonth = async (increment: number) => {
      const newMonth = currentMonth.value + increment
      if (newMonth > 11) {
        currentMonth.value = 0
        currentYear.value++
      } else if (newMonth < 0) {
        currentMonth.value = 11
        currentYear.value--
      } else {
        currentMonth.value = newMonth
      }
      fetchMobilizations()
    }

    const nextMonth = () => changeMonth(1)
    const previousMonth = () => changeMonth(-1)

    const getDaysForGrid = (year: number, month: number) => {
      const daysInMonth = new Date(year, month + 1, 0).getDate()
      const firstDayOfMonth = new Date(year, month, 1).getDay()
      const days = []

      // Adjusting for weeks starting from Monday
      const daysFromPrevMonth = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1

      // Adding days from the previous month
      for (let i = daysFromPrevMonth; i > 0; i--) {
        const day = new Date(year, month, -i + 1)
        days.push({ date: day, isCurrentMonth: false })
      }

      // Adding days of the current month
      for (let i = 1; i <= daysInMonth; i++) {
        const day = new Date(year, month, i)
        days.push({ date: day, isCurrentMonth: true })
      }

      // Adding days of the next month
      const totalDays = 42 // Total grid cells
      while (days.length < totalDays) {
        const nextDayIndex: number = days.length - daysInMonth - daysFromPrevMonth + 1
        const day = new Date(year, month + 1, nextDayIndex)
        days.push({ date: day, isCurrentMonth: false })
      }

      return days
    }

    const daysForGrid = computed(() => {
      return getDaysForGrid(currentYear.value, currentMonth.value)
    })

    const setSelectedDay = (day: Date) => {
      try {
        store.dispatch('mobilization/setSelectedDayAndFetchData', day)
      } catch (error) {
        console.error('Error fetching mobilizations', error)
      }
    }

    setSelectedDay(new Date()) // Set default date and fetch

    const selectedDay = computed(() => store.getters['mobilization/getSelectedDay'])

    const mobilizationDetails = computed(() => {
      const mobs = store.getters['mobilization/getMobilizationDetails']
      return mobs
    })

    const mobilizations = computed(() => {
      const counts = store.getters['mobilization/getMonthlyMobilizations']
      return counts
    })

    const getDotCount = (date: Date) => {
      if (mobilizations.value) {
        const countData = mobilizations.value.filter((data: any) => new Date(data.departureDate) <= date && date <= new Date(data.returnDate)).length
        return countData
      }

      return 0
    }

    const fetchMobilizations = async () => {
      try {
        await store.dispatch('mobilization/fetchMobilizationsForMonth', { year: currentYear.value, month: currentMonth.value + 1 })
      } catch (error) {
        console.error('Error fetching mobilizations', error)
      }
    }

    const isSelectedDay = (day: Date) => {
      const selectedDay = store.getters['mobilization/getSelectedDay']
      const isSelected = selectedDay && day.toDateString() === new Date(selectedDay).toDateString()
      return isSelected
    }

    fetchMobilizations()

    return { weekDays, currentDateDisplay, nextMonth, previousMonth, daysForGrid, setSelectedDay, isSelectedDay, mobilizationDetails, selectedDay, getDotCount }
  },
  methods: {
    displayFormattedDate (date: Date) {
      if (date) {
        return formatDateToString(date)
      }
    }
  }
})
</script>
